/**
 * Kory's screen digest: what a sighted person sees, read from the accessibility
 * tree (roles, accessible names, landmarks, states), so Kory knows what you're
 * looking at. It rides inside the `<page-context>` block every typed and spoken
 * turn already carries, and is rebuilt on each capture.
 *
 * Field values never enter it: names come from labels, never from what is typed.
 * When the page isn't shared (the This page chip's "Nothing from this page", the
 * default on payroll, commissions and HR routes) it keeps only Kory's own surfaces
 * (the tray, an open to-do drawer, the viewport and focus).
 */
import { computeAccessibleName, getRole } from "dom-accessibility-api";

import { SIDE_FROM } from "@/components/ui/docked-tray";
import { MOBILE_BREAKPOINT } from "@/hooks/use-mobile";
import { readKoryNouns, readPageLevelNouns } from "@/lib/kory-nouns";
import { isPagePart } from "@/lib/kory-refs";

/** The digest's ceiling, well inside the page block's budget. */
export const SCREEN_DIGEST_MAX_CHARS = 4_000;
const MAX_LANDMARKS = 16;
const MAX_HEADINGS = 12;
const MAX_CARDS = 12;
const MAX_RECORDS = 20;
const MAX_PARTS = 10;
const MAX_NAME = 80;

/** Payroll, commissions and HR: nothing from the page itself goes to Kory by default (`kory-page-sharing`). */
const SENSITIVE_ROUTES = [
  "/finance/commissions",
  "/profile/commission-statements",
  "/finance/overhead",
  "/finance/margins",
  "/people",
  "/profile/time-off",
];

export function isSensitiveRoute(pathname: string): boolean {
  return SENSITIVE_ROUTES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function viewportClass(width: number): "phone" | "tablet" | "desktop" {
  if (width < MOBILE_BREAKPOINT) return "phone";
  return width < SIDE_FROM ? "tablet" : "desktop";
}

/** A Kory tray (the global one or Overview's): it carries its detent. */
const TRAY = "[data-detent]";
const DIALOG = '[role="dialog"], [role="alertdialog"]';
const CARD = "[data-todo-id]";
/** A Kory nudge row in the tray (Since you left). */
const NUDGE = "[data-nudge]";
const LANDMARK =
  'header, nav, main, aside, footer, section, form, [role="banner"], [role="navigation"], [role="main"], ' +
  '[role="complementary"], [role="contentinfo"], [role="region"], [role="search"], [role="form"]';
/** Live cards Kory is still drafting carry this id prefix (`PENDING_PREFIX` in the tray). */
const DRAFTING_PREFIX = "pending:";

function clean(text: string): string {
  const one = text.replace(/\s+/g, " ").trim();
  return one.length > MAX_NAME ? `${one.slice(0, MAX_NAME - 1)}…` : one;
}

/** The page's heading, whitespace collapsed and capped; empty when the page has none. */
export function pageHeading(): string {
  const h1 = document.querySelector("main h1") ?? document.querySelector("h1");
  return h1 ? clean(h1.textContent ?? "") : "";
}

/** Only the app area a route is in (`/finance/commissions/bellmech?run=4` → `/finance`): nothing after
 *  it, where a record id, a tenant slug or a period would be. What an unshared page sends for where you are. */
export function routeArea(pathname: string): string {
  return `/${pathname.split("/").filter(Boolean)[0] ?? ""}`;
}

function nameOf(el: Element): string {
  return clean(computeAccessibleName(el));
}

/** Hidden from a sighted person: hidden, inert or aria-hidden, or not rendered visible. */
export function hiddenFromView(el: Element): boolean {
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return true;
  return typeof el.checkVisibility === "function" && !el.checkVisibility({ visibilityProperty: true });
}

/** On screen: not hidden from view, and inside the viewport where layout exists. */
function shown(el: Element): boolean {
  if (hiddenFromView(el)) return false;
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return true; // no layout (tests): visibility above decides
  return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
}

const clips = (overflow: string) => overflow !== "" && overflow !== "visible";

/** Where `el` can be seen: its box cut down by every ancestor that clips (a scrolling table, a card
 *  that hides overflow) and by the window. Null when nothing of it shows. */
export function visibleBox(el: Element): { top: number; right: number; bottom: number; left: number } | null {
  const box = el.getBoundingClientRect();
  let { top, right, bottom, left } = box;
  for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
    const style = getComputedStyle(a);
    if (!clips(style.overflowX) && !clips(style.overflowY)) continue;
    const clip = a.getBoundingClientRect();
    top = Math.max(top, clip.top);
    right = Math.min(right, clip.right);
    bottom = Math.min(bottom, clip.bottom);
    left = Math.max(left, clip.left);
  }
  top = Math.max(top, 0);
  bottom = Math.min(bottom, window.innerHeight);
  return box.width && bottom > top && right > left ? { top, right, bottom, left } : null;
}

/** Scrolled out of a container that clips it (a ColumnList row scrolled away): nothing of it shows,
 *  though it sits inside the window. Never true without layout (tests), where ``shown`` decides. */
function clippedAway(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return Boolean(r.width || r.height) && !visibleBox(el);
}

/** Kory's own surfaces: a tray, or the to-do drawer. */
function koryOwned(el: Element): boolean {
  return Boolean(el.closest(`${TRAY}, [data-kory-todo]`));
}

function trayLine(tray: Element): string {
  const label = nameOf(tray) || "Kory";
  if (tray.hasAttribute("data-side")) {
    return `${label} tray: side pane, ${tray.getAttribute("data-detent") === "peek" ? "folded" : "open"}`;
  }
  return `${label} tray: bottom sheet, ${tray.getAttribute("data-detent")}`;
}

function dialogLine(dialog: Element, named: boolean): string {
  const todo = dialog.getAttribute("data-kory-todo");
  if (todo) return `open to-do drawer: "${nameOf(dialog)}" (to-do ${todo})`;
  return named ? `open dialog: "${nameOf(dialog)}"` : "open dialog";
}

function cardLine(card: HTMLElement, n: number): string {
  const id = card.dataset.todoId ?? "";
  const drafting = id.startsWith(DRAFTING_PREFIX);
  const states = [
    drafting ? "Kory is drafting it" : `to-do ${id}`,
    card.closest(TRAY) ? "in the tray" : null,
    card.hasAttribute("data-named") ? "highlighted" : null,
    // The card they opened with a tap: what "this one" means on their next turn.
    card.hasAttribute("data-opened") ? "they opened it" : null,
    [null, "false"].includes(card.getAttribute("aria-current")) ? null : "open",
  ].filter(Boolean);
  return `${n}. "${nameOf(card).replace(/^(Open|Show|Hide) /, "")}" (${states.join(", ")})`;
}

/** The digest as a `<screen>` block, or null outside a browser or on an empty page. With `koryOnly`
 *  (the page isn't shared) only Kory's own surfaces are described. */
export function screenDigest({ koryOnly }: { koryOnly: boolean }): string | null {
  if (typeof window === "undefined") return null;
  const allowed = (el: Element) => !koryOnly || koryOwned(el);
  const lines: string[] = [];

  // An unshared page's heading and ids can carry its data (a commission run's family and period).
  const h1 = koryOnly ? "" : pageHeading();
  const route = koryOnly ? routeArea(window.location.pathname) : window.location.pathname;
  lines.push(`route: ${route}${h1 ? ` · heading "${h1}"` : ""}`);
  const { innerWidth: w, innerHeight: h } = window;
  lines.push(`viewport: ${viewportClass(w)} ${w}×${h}, ${w >= h ? "landscape" : "portrait"}`);

  for (const tray of document.querySelectorAll(TRAY)) if (shown(tray)) lines.push(trayLine(tray));
  for (const dialog of document.querySelectorAll(DIALOG)) if (shown(dialog)) lines.push(dialogLine(dialog, allowed(dialog)));

  const focused = document.activeElement;
  if (focused && focused !== document.body && focused !== document.documentElement) {
    const name = allowed(focused) ? nameOf(focused) : "";
    lines.push(`focus: ${getRole(focused) ?? focused.tagName.toLowerCase()}${name ? ` "${name}"` : ""}`);
  }

  const landmarks: string[] = [];
  for (const el of document.querySelectorAll(LANDMARK)) {
    if (landmarks.length >= MAX_LANDMARKS) break;
    if (!allowed(el) || !shown(el)) continue;
    const role = getRole(el);
    const name = nameOf(el);
    // Unnamed sections and forms aren't landmarks; an unnamed main still is.
    if (!role || (!name && role !== "main")) continue;
    const entry = name ? `${role} "${name}"` : role;
    if (!landmarks.includes(entry)) landmarks.push(entry);
  }
  if (landmarks.length) lines.push(`landmarks: ${landmarks.join(", ")}`);

  // The page's own headings below its title, by name: something Kory can point at as `heading "…"`.
  const headings: string[] = [];
  for (const el of document.querySelectorAll('h2, h3, h4, [role="heading"]')) {
    if (headings.length >= MAX_HEADINGS) break;
    if (!allowed(el) || koryOwned(el) || !shown(el)) continue;
    const name = nameOf(el);
    if (name && !headings.includes(`"${name}"`)) headings.push(`"${name}"`);
  }
  if (headings.length) lines.push(`headings: ${headings.join(", ")}`);

  const cards = [...document.querySelectorAll<HTMLElement>(CARD)].filter((c) => allowed(c) && shown(c));
  if (cards.length) {
    lines.push("cards on screen, in order:");
    // The card they opened is always listed, even past the cap: it is what "this one" means.
    const listed = cards.flatMap((card, i) => (i < MAX_CARDS || card.hasAttribute("data-opened") ? [cardLine(card, i + 1)] : []));
    lines.push(...listed);
    if (cards.length > listed.length) lines.push(`…and ${cards.length - listed.length} more`);
  }
  // What Kory can point at on screen, by the ref each element carries (``data-kory-ref``): the records, by
  // the noun Kory points at them with (named by the page's label for it, else by the element's name), and
  // the page parts, by their ref (named by the element's name). To-dos are the cards above; a ``<type>:*``
  // stands for no one record. An element scrolled away is not listed.
  const records: string[] = [];
  const parts: string[] = [];
  const add = (list: string[], cap: number, ref: string, label: string | null | undefined) => {
    if (list.length < cap && !list.some((entry) => entry.startsWith(`${ref} `) || entry === ref)) {
      list.push(label ? `${ref} "${clean(label)}"` : ref);
    }
  };
  const pageRecords: string[] = [];
  if (!koryOnly) {
    const labels = new Map(readKoryNouns().map((noun) => [`${noun.type}:${noun.id}`, noun.label]));
    for (const el of document.querySelectorAll<HTMLElement>("[data-kory-ref]")) {
      if (!shown(el) || clippedAway(el)) continue;
      for (const ref of el.dataset.koryRef?.split(" ") ?? []) {
        if (isPagePart(ref)) add(parts, MAX_PARTS, ref, nameOf(el));
        else if (ref.includes(":") && !ref.startsWith("todo:") && !ref.endsWith(":*")) {
          add(records, MAX_RECORDS, ref, labels.get(ref) || nameOf(el));
        }
      }
    }
    for (const noun of readPageLevelNouns().slice(0, MAX_RECORDS)) {
      pageRecords.push(noun.label ? `${noun.type}:${noun.id} "${clean(noun.label)}"` : `${noun.type}:${noun.id}`);
    }
  }
  if (records.length) lines.push(`records on screen (point with nouns): ${records.join(", ")}`);
  if (parts.length) lines.push(`page parts on screen (point with targets, by ref): ${parts.join(", ")}`);
  // No row draws these: Kory points at the page's parts by role and name instead.
  if (pageRecords.length) lines.push(`this page is about (point at its parts by role and name): ${pageRecords.join(", ")}`);
  const nudge = document.querySelector(`${NUDGE}[data-opened]`);
  if (nudge && allowed(nudge) && shown(nudge)) lines.push(`they opened the nudge "${nameOf(nudge).replace(/^Hide /, "")}"`);

  // Only the route and viewport: a page with nothing on it (an embed, a blank shell) sends nothing.
  if (lines.length === 2 && !h1) return null;
  const body = lines.join("\n").slice(0, SCREEN_DIGEST_MAX_CHARS);
  return `<screen>\n${body}\n</screen>`;
}
